import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAppContext } from "./AppContext";
import { useAuth } from "./AuthContext";
import websocketService from "../utils/websocket";

// Create WebSocket context
const WebSocketContext = createContext();

// Provider component
export const WebSocketProvider = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const { userData } = useAppContext();
  const { isAuthenticated } = useAuth();

  // Connect to WebSocket when user data is available
  useEffect(() => {
    if (isAuthenticated && userData && userData.patientId) {
      console.log("Connecting to WebSocket for patient:", userData.patientId);
      websocketService.connect(userData.patientId);
    }
  }, [isAuthenticated, userData]);

  // Set up event listeners
  useEffect(() => {
    const handleConnection = (data) => {
      console.log("WebSocket connection status:", data.status);
      setConnectionStatus(data.status);
      if (data.status === "connected") {
        setError(null);
      }
    };

    const handleMessage = (data) => {
      setLastMessage(data);
      console.log("WebSocket message received in context:", data);
    };

    const handleError = (error) => {
      setError(error.message);
      console.error("WebSocket error in context:", error);
    };

    const handleReconnect = (data) => {
      console.log("WebSocket reconnect attempt:", data.attempt);
      // You can add UI feedback here if needed
    };

    const handleCheckoutDone = (data) => {
      console.log("WebSocket checkout done:", data);
    };

    // Register event listeners
    websocketService.on("connection", handleConnection);
    websocketService.on("message", handleMessage);
    websocketService.on("error", handleError);
    websocketService.on("reconnect", handleReconnect);
    websocketService.on("checkout_done", handleCheckoutDone);

    // Cleanup on unmount
    return () => {
      websocketService.off("connection", handleConnection);
      websocketService.off("message", handleMessage);
      websocketService.off("error", handleError);
      websocketService.off("reconnect", handleReconnect);
    };
  }, []);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      websocketService.disconnect();
    };
  }, []);

  // Send message function
  const sendMessage = useCallback((type, payload) => {
    websocketService.send(type, payload);
  }, []);

  // Subscribe to specific events
  const subscribe = useCallback((eventType, handler) => {
    websocketService.on(eventType, handler);

    // Return unsubscribe function
    return () => {
      websocketService.off(eventType, handler);
    };
  }, []);

  // Get connection status
  const getConnectionStatus = useCallback(() => {
    return websocketService.getStatus();
  }, []);

  // Check if connected
  const isConnected = useCallback(() => {
    return websocketService.isConnected();
  }, []);

  // Reconnect manually
  const reconnect = useCallback(() => {
    if (userData && userData.patientId) {
      websocketService.disconnect();
      websocketService.connect(userData.patientId);
    }
  }, [userData]);

  const value = {
    connectionStatus,
    lastMessage,
    error,
    sendMessage,
    subscribe,
    getConnectionStatus,
    isConnected,
    reconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook to use WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
